import type { JSX, ReactNode } from 'react';

import { Filtermarken, type Filtermarke } from './Filtermarken';
import { Sinnbild } from './Sinnbild';

/**
 * DER SEITENTYP LISTE (ÄA-41, Entwurf v2) — ein Baustein für alle Listen.
 *
 * Kopf mit Zähler und Haupthandlung stehen im Seitenkopf (`useZaehler`, die
 * Handlung der Arbeitsfläche); dieser Baustein trägt, was darunter kommt:
 *
 *   * die FILTERLEISTE auf dem Grund — die Suche weiß mit Lupe, daneben die
 *     Filter der Liste (Auswahl, Segmente, Schalter) und die aktiven Filter
 *     als abwählbare Marken;
 *   * HINWEISE darunter — Suchhilfe, „noch zwei Zeichen", „mehr als 500";
 *   * die RASTERTABELLE in einer Karte, rechts daneben die VORSCHAU, wo die
 *     Liste eine hat.
 *
 * Die Suche trägt `liste__suche`: Der Schrägstrich springt hinein (Tab. 3).
 */
export function Liste({
  suche,
  filter,
  marken = [],
  hinweise,
  vorschau,
  vorschauBeschriftung = 'Vorschau',
  vorschauNurMitZeiger = false,
  children,
}: {
  readonly suche?: {
    readonly wert: string;
    readonly aendern: (wert: string) => void;
    readonly beschriftung: string;
    readonly platzhalter?: string;
    /** Die Kennung einer Suchhilfe unter der Leiste (`aria-describedby`). */
    readonly beschrieben?: string;
  };
  readonly filter?: ReactNode;
  readonly marken?: readonly Filtermarke[];
  readonly hinweise?: ReactNode;
  /** Die Vorschau rechts; ohne sie nimmt die Tabelle die volle Breite. */
  readonly vorschau?: ReactNode;
  /**
   * Der Name der Seitenleiste für Vorlesehilfen. „Vorschau", wo sie nur
   * zeigt; wo in ihr bearbeitet wird (Absagen, Widerrufe), sagt sie das.
   */
  readonly vorschauBeschriftung?: string;
  /**
   * Die Vorschau folgt nur dem Überfahren und hat keinen eigenen Inhalt
   * (Kundenliste). Auf dem Telefon fehlt sie dann: Dort gibt es kein
   * Überfahren, und das Tippen öffnet ohnehin den Datensatz (Auftrag CRM-105).
   */
  readonly vorschauNurMitZeiger?: boolean;
  /** Die Tabelle und ihr Leerzustand — sie stehen in der Karte. */
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="liste">
      {(suche !== undefined || filter !== undefined || marken.length > 0) && (
        <div className="filterleiste">
          {suche !== undefined && (
            <label className="filterleiste__suche">
              <Sinnbild name="magnifying-glass" />
              <input
                className="liste__suche"
                type="search"
                value={suche.wert}
                onChange={(e) => suche.aendern(e.target.value)}
                placeholder={suche.platzhalter}
                aria-label={suche.beschriftung}
                {...(suche.beschrieben === undefined ? {} : { 'aria-describedby': suche.beschrieben })}
              />
            </label>
          )}
          {filter}
          <Filtermarken marken={marken} />
        </div>
      )}

      {hinweise !== undefined && <div className="liste__hinweise">{hinweise}</div>}

      {vorschau === undefined ? (
        <div className="karte karte--tabelle">{children}</div>
      ) : (
        <div className="liste__raster">
          <div className="karte karte--tabelle">{children}</div>
          <aside
            className={vorschauNurMitZeiger ? 'karte liste__vorschau liste__vorschau--zeiger' : 'karte liste__vorschau'}
            aria-label={vorschauBeschriftung}
          >
            {vorschau}
          </aside>
        </div>
      )}
    </div>
  );
}
